import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaGoogle, FaTripadvisor, FaMicrosoft } from 'react-icons/fa';
import { MdRestaurantMenu } from 'react-icons/md';
import { useGoogleBusinessStatus } from '../hooks/useGoogleBusiness';

interface Integration {
  id: string;
  name: string;
  description: string;
  logoColor: string;
  logoIcon?: React.ReactNode;
  available: boolean;
}

const integrations: Integration[] = [
  {
    id: 'google-calendar',
    name: 'Google Calendar',
    description: 'Schichten automatisch in Google Kalender synchronisieren',
    logoColor: '#4285F4',
    logoIcon: <FaGoogle size={36} />,
    available: true,
  },
  {
    id: 'outlook-calendar',
    name: 'Outlook Calendar',
    description: 'Schichten automatisch in Outlook Kalender synchronisieren',
    logoColor: '#0078D4',
    logoIcon: <FaMicrosoft size={36} />,
    available: true,
  },
  {
    id: 'datev',
    name: 'DATEV',
    description: 'Lohnabrechnung und Buchhaltung',
    logoColor: '#005AA0',
    available: true,
  },
  {
    id: 'lightspeed',
    name: 'Lightspeed',
    description: 'Kassensystem (K-Series) – Umsatzimport',
    logoColor: '#00B388',
    available: true,
  },
  {
    id: 'orderbird',
    name: 'orderbird',
    description: 'iPad Kassensystem',
    logoColor: '#FF6B35',
    available: false,
  },
  {
    id: 'vectron',
    name: 'Vectron',
    description: 'Kassensystem und POS',
    logoColor: '#0066CC',
    available: false,
  },
  {
    id: 'shopify',
    name: 'Shopify',
    description: 'E-Commerce Platform',
    logoColor: '#96BF48',
    available: false,
  },
  {
    id: 'ready-to-order',
    name: 'Ready to Order',
    description: 'Online Bestellsystem',
    logoColor: '#E85D75',
    logoIcon: <MdRestaurantMenu size={40} />,
    available: false,
  },
  {
    id: 'google-business',
    name: 'Google Business Profile',
    description: 'Google Reviews ansehen & beantworten',
    logoColor: '#4285F4',
    logoIcon: <FaGoogle size={36} />,
    available: true,
  },
  {
    id: 'tripadvisor',
    name: 'TripAdvisor',
    description: 'Reisebewertungen',
    logoColor: '#00AF87',
    logoIcon: <FaTripadvisor size={40} />,
    available: false,
  },
];

const Integrations: React.FC = () => {
  const navigate = useNavigate();
  const { status: googleBusinessStatus } = useGoogleBusinessStatus();
  const [hoveredId, setHoveredId] = React.useState<string | null>(null);

  const connectionStatuses: Record<string, boolean> = React.useMemo(() => {
    const statuses: Record<string, boolean> = {};
    if (googleBusinessStatus?.connected) {
      statuses['google-business'] = true;
    }
    return statuses;
  }, [googleBusinessStatus]);

  const handleCardClick = (integration: Integration) => {
    if (integration.available) {
      navigate(`/settings/integrations/${integration.id}`);
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 1400 }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 24, marginBottom: 8, color: '#0f2b5a' }}>Integrationen</h1>
        <p style={{ fontSize: 14, color: '#666', margin: 0 }}>
          Verbinden Sie SundaeOS mit Ihren Lieblings-Tools und Services.
        </p>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 20,
        }}
      >
        {integrations.map((integration) => {
          const isConnected = connectionStatuses[integration.id] ?? false;
          const isHovered = hoveredId === integration.id;

          return (
            <div
              key={integration.id}
              onClick={() => handleCardClick(integration)}
              style={{
                background: '#fff',
                border: `2px solid ${integration.available && isHovered ? '#021a4a' : integration.available ? '#e0e3ef' : '#f0f0f0'}`,
                borderRadius: 12,
                padding: 24,
                cursor: integration.available ? 'pointer' : 'not-allowed',
                opacity: integration.available ? 1 : 0.5,
                transition: 'all 0.2s ease',
                position: 'relative',
                boxShadow: integration.available && isHovered ? '0 4px 12px rgba(2, 26, 74, 0.1)' : 'none',
              }}
              onMouseEnter={() => {
                if (integration.available) setHoveredId(integration.id);
              }}
              onMouseLeave={() => {
                if (integration.available) setHoveredId(null);
              }}
            >
              {!integration.available && (
                <div
                  style={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    padding: '4px 12px',
                    background: '#f8f9fa',
                    border: '1px solid #d0d4e4',
                    borderRadius: 12,
                    fontSize: 11,
                    fontWeight: 600,
                    color: '#666',
                  }}
                >
                  Bald verfügbar
                </div>
              )}

              {isConnected && (
                <div
                  style={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    padding: '4px 12px',
                    background: '#e8f5e9',
                    border: '1px solid #4caf50',
                    borderRadius: 12,
                    fontSize: 11,
                    fontWeight: 600,
                    color: '#2e7d32',
                  }}
                >
                  Verbunden
                </div>
              )}

              <div
                style={{
                  width: 80,
                  height: 80,
                  margin: '0 auto 16px',
                  borderRadius: 12,
                  background: integration.logoColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 32,
                  fontWeight: 700,
                  color: '#fff',
                  textTransform: 'uppercase',
                }}
              >
                {integration.logoIcon || integration.name.substring(0, 1)}
              </div>

              <h3
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  marginBottom: 8,
                  color: integration.available ? '#0f2b5a' : '#999',
                }}
              >
                {integration.name}
              </h3>

              <p
                style={{
                  fontSize: 14,
                  color: integration.available ? '#666' : '#999',
                  margin: 0,
                  lineHeight: 1.5,
                }}
              >
                {integration.description}
              </p>

              {integration.available && (
                <div
                  style={{
                    marginTop: 16,
                    paddingTop: 16,
                    borderTop: '1px solid #e0e3ef',
                    fontSize: 13,
                    color: '#021a4a',
                    fontWeight: 600,
                  }}
                >
                  Konfigurieren →
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Integrations;
